/** @file Defines the classes and utility functions to handle datetimes and moments. */
import moment from 'moment';
import { Maybe } from '@codegrade/maybe';
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export class RelativeTime {
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    time = performance.now();
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor() {
        Object.freeze(this);
    }
    /** @returns Get the amount of time since this relative time object. */
    since() {
        return moment.duration(performance.now() - this.time, 'ms');
    }
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function momentUtc(correction) {
    const now = moment.utc();
    return correction.mapOrDefault(delta => now.add(delta), now);
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function epoch() {
    return moment.utc(0);
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
function hasTimezoneInfo(date) {
    return date.endsWith('Z') || date.endsWith('+00:00') || /\+\d\d:\d\d$/.test(date);
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function toMoment(date) {
    if (moment.isMoment(date)) {
        return date.clone();
    }
    else if (date instanceof Date) {
        return moment.utc(date);
    }
    else {
        // Use the fast path if possible. Parsing with `Date` is way faster,
        // however it will assume local time if it encounters a isoformat
        // without timezone specifier, while we expect it to parse it as a UTC
        // time in that case. We also check if it is a string as this code is
        // also used from non typed javascript files.
        if (typeof date === 'string' && hasTimezoneInfo(date)) {
            const dateNumber = Date.parse(date);
            if (!Number.isNaN(dateNumber)) {
                return moment.utc(dateNumber);
            }
        }
        return moment.utc(date, moment.ISO_8601, true);
    }
}
/**
 * Converts a moment or a string to a Just<Moment>.
 * Undefined, null, and invalid inputs will produce a Nothing.
 *
 * @param date The date to be converted.
 * @returns The date converted to a moment and wrapped in a Maybe.
 */
export function toMomentMaybe(date) {
    return Maybe.fromNullable(date)
        .map(toMoment)
        .filter(mom => mom.isValid());
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export const toMomentNullable = (...args) => toMomentMaybe(...args).extractNullable();
/**
 * Get the difference between two moments as a duration.
 *
 * @param from The start of your interval.
 * @param to The end of your interval.
 * @returns The duration between `from` and `to`.
 */
export function diff(from, to) {
    return moment.duration(toMoment(to).diff(from, 'ms'), 'ms');
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function formatMoment(date, format) {
    return toMoment(date).local().format(format); // ignore: no-raw-format
}
/**
 * Create a readable string representation of a moment containing only the date.
 *
 * @param date The moment to format into a readable string.
 * @returns The date formatted as 'MMM DD, YYYY' i.e. Sep 23, 2024.
 */
export function readableDateOnly(date) {
    return formatMoment(date, 'MMM DD, YYYY');
}
/**
 * Create a readable string representation of a moment containing only the time.
 *
 * @param date The moment to format into a readable string.
 * @returns The date formatted as HH:mm i.e. 10:21.
 */
export function readableTimeOnly(date) {
    return formatMoment(date, 'HH:mm');
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function readableFormatDate(date) {
    return formatMoment(date, 'YYYY-MM-DD HH:mm');
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function formatDate(date, iso = false) {
    if (iso) {
        return toMoment(date).utc().toISOString();
    }
    else {
        return formatMoment(date, 'YYYY-MM-DDTHH:mm');
    }
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function formatNullableDate(date, iso = false) {
    if (date == null) {
        return null;
    }
    return formatDate(date, iso);
}
/** A class that represent a date object, containing year, month and day. */
export class DateWithoutTime {
    year;
    month;
    day;
    /** This class is not an interface. */
    _tag;
    /**
     * Construct a new DateWithoutTime instance.
     *
     * @param year The year of the date.
     * @param month The month of the date (1 indexed, meaning 1: January).
     *  Note: We have to use 1:indexed because we use this class to represent the contract start
     *  dates for tenants from the postgres database, these use datetime.date, which is 1 indexed.
     * @param day The day of the date.
     */
    constructor(year, month, day) {
        this.year = year;
        this.month = month;
        this.day = day;
        Object.freeze(this);
    }
    /**
     * Convert the moment to a date without time.
     *
     * @param date The original moment from which to get the date without time.
     * @returns A new DateWithoutTime instance with the same date without time as the original moment.
     */
    static fromMoment(date) {
        return new DateWithoutTime(date.year(), date.month() + 1, date.date());
    }
    /** @returns The date in readable date only format. */
    readableFormat() {
        return readableDateOnly(moment({ year: this.year, month: this.month - 1, day: this.day }));
    }
    /**
     * Does this date equal to the provided date.
     *
     * @param date The date to compare with.
     * @returns Whether this date is equal to the provided date.
     */
    eq(date) {
        return this.compare(date) === 0;
    }
    /**
     * Compare this date with the provided date to determine ordering.
     *
     * We use numbers here to signify the order:
     *  < 0 if this date is less than the provided date.
     *  0 if this date is equal to the provided date.
     *  > 0 if this date is greater than the provided date.
     *
     * @param date The date to compare with.
     * @returns The ordering of this date compared to the provided date.
     */
    compare(date) {
        for (const attr of ['year', 'month', 'day']) {
            const difference = this[attr] - date[attr];
            if (difference !== 0) {
                return difference;
            }
        }
        return 0;
    }
}
const MAXIMUM_MOMENT = moment.utc({
    year: 9999,
    month: 11,
    day: 31,
});
/**
 * Get the maximum possible moment instance that is still representable as
 * sortable ISO string.
 *
 * Yes technically this is incorrect, but the software will 100% not be used
 * anymore in the year 9999, so it's fine.
 *
 * @returns The a moment with the maximum possible value.
 */
export function getMaximumMoment() {
    return MAXIMUM_MOMENT.clone();
}
const MINIMUM_MOMENT = moment.utc({
    year: 1,
    month: 0,
    day: 1,
});
/**
 * Get the minimum possible moment instance that is still representable as
 * sortable ISO string.
 *
 * Yes technically this is incorrect, but the software was not used before year
 * 1 anyway.
 *
 * @returns The a moment with the minimum possible value.
 */
export function getMinimumMoment() {
    return MINIMUM_MOMENT.clone();
}
